/**
 * canvas-engine/model/assetRef.js
 *
 * OWN, self-contained asset reference normalization for the Canvas Engine.
 *
 * ZERO-REUSE CONSTRAINT: imports NOTHING from the existing builder or reader.
 *
 * Single image / asset representation (Req 22.4):
 * All image-valued properties are stored as an asset object
 * `{ storedLink, displayLink }`:
 *   - `storedLink`  — the durable key/URL persisted with the template.
 *   - `displayLink` — the resolvable (often pre-signed) URL used for rendering.
 *
 * Rules:
 *   - Plain-string URLs are normalized into `{ storedLink, displayLink }` on read.
 *   - `null`/`undefined` (and any unusable value) clears to the empty asset
 *     `{ storedLink: '', displayLink: '' }`.
 *   - Existing `{ storedLink, displayLink }` objects round-trip unchanged.
 *
 * Pure functions only — no React, no DOM, no side effects.
 */

/**
 * Canonical empty asset reference. Returned by a clear (`null`) and by any
 * unusable input. Treat as a value; callers must not mutate it.
 */
export const EMPTY_ASSET = Object.freeze({ storedLink: '', displayLink: '' });

/**
 * Coerce a candidate field into a trimmed string, or '' when not a string.
 * @param {*} v
 * @returns {string}
 */
function toLink(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Normalize any asset-shaped value into a fresh `{ storedLink, displayLink }`.
 *
 * - `null` / `undefined` / non-usable  → `{ storedLink: '', displayLink: '' }`
 * - plain string URL                   → `{ storedLink: url, displayLink: url }`
 * - object                             → reads common link aliases and mirrors
 *                                        a present link into the missing side.
 *
 * Always returns a new plain object (never the frozen EMPTY_ASSET, never the
 * caller's input), so the result is safe to store and mutate.
 *
 * @param {*} v
 * @returns {{storedLink: string, displayLink: string}}
 */
export function normalizeAsset(v) {
  // Clear / unusable → empty asset.
  if (v == null) {
    return { storedLink: '', displayLink: '' };
  }

  // Plain-string URL → both links point at it.
  if (typeof v === 'string') {
    const link = v.trim();
    return { storedLink: link, displayLink: link };
  }

  // Object → read common aliases, mirror present link into the missing side.
  if (typeof v === 'object') {
    const stored = toLink(v.storedLink) || toLink(v.stored) || toLink(v.key) || toLink(v.url) || toLink(v.src);
    const display = toLink(v.displayLink) || toLink(v.display) || toLink(v.url) || toLink(v.src);

    const storedLink = stored || display;
    const displayLink = display || stored;
    return { storedLink, displayLink };
  }

  // Numbers, booleans, etc. → empty asset.
  return { storedLink: '', displayLink: '' };
}

/**
 * Public alias used by renderers/controls when reading an asset value.
 * Identical behavior to `normalizeAsset`. (design: model/assetRef key exports)
 *
 * @param {*} v
 * @returns {{storedLink: string, displayLink: string}}
 */
export function assetRef(v) {
  return normalizeAsset(v);
}

/**
 * True when an asset reference carries no usable link.
 * @param {*} v
 * @returns {boolean}
 */
export function isEmptyAsset(v) {
  const a = normalizeAsset(v);
  return a.storedLink === '' && a.displayLink === '';
}
